import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token && user) {
      fetchCart();
    } else {
      setCart({ items: [], total: 0 });
    }
  }, [token, user]);

  const fetchCart = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCart(data);
      }
    } catch (e) {
      console.error('Cart fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    if (!token) throw new Error('Please login to add items');
    
    const res = await fetch(`${API}/cart/add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ product_id: productId, quantity })
    });
    
    if (!res.ok) throw new Error('Failed to add to cart');
    await fetchCart();
  };

  const updateCartItem = async (productId, quantity) => {
    if (!token) return;
    
    const res = await fetch(`${API}/cart/update`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ product_id: productId, quantity })
    });
    
    if (!res.ok) throw new Error('Failed to update cart');
    await fetchCart();
  };

  const removeFromCart = async (productId) => {
    if (!token) return;
    
    const res = await fetch(`${API}/cart/remove/${productId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (!res.ok) throw new Error('Failed to remove from cart');
    await fetchCart();
  };

  const clearCart = async () => {
    if (!token) return;
    
    await fetch(`${API}/cart/clear`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    setCart({ items: [], total: 0 });
  };

  const itemCount = cart.items?.reduce((acc, item) => acc + item.quantity, 0) || 0;

  return (
    <CartContext.Provider value={{
      cart, loading, addToCart, updateCartItem, removeFromCart, clearCart, fetchCart, itemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
